// src/firestoreFunctions.js
import { getUserId, getToken, onAuthStateChanged } from '../firebase';
import axios from "axios";

const backendURL = `${location.protocol}//${location.hostname}/api/server`;
const llmURL = `${location.protocol}//${location.hostname}/api/llm`;

const authorizedRequest = async (method, path, { params, data } = {}) => {
  const token = await getToken();
  const config = {
    method,
    url: `${backendURL}${path}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  if (params) {
    config.params = params;
  }

  if (data !== undefined) {
    config.data = data;
  }

  return axios(config);
};

const serverGet = (path, params) => authorizedRequest('get', path, { params });
const serverPost = (path, data) => authorizedRequest('post', path, { data });
const serverPut = (path, data) => authorizedRequest('put', path, { data });
const serverPatch = (path, data) => authorizedRequest('patch', path, { data });
const serverDelete = (path) => authorizedRequest('delete', path);

// Function to add a staff member
export const addStaffMember = async (workgroupId, email, role = "member") => {
  try {
    const response = await serverPost(`/workgroups/${workgroupId}/members`, {
      email,
      role,
    });
    return response.data;
  } catch (error) {
    const apiError = error.response?.data?.error;
    if (apiError) {
      throw new Error(`Failed to add staff member: ${apiError}`);
    }
    throw new Error(
      `Failed to add staff member: ${error.message || "Unknown error"}`
    );
  }
};

// Function to update staff list and handle errors
export const updateStaffList = async (workgroupId, setStaff, setError, setLoading) => {
  setLoading(true); // Start loading state
  setError(null); // Clear any previous errors
  try {
    const staffList = await fetchStaff(workgroupId); // Fetch staff
    setStaff(staffList); // Update the staff list in state
  } catch (error) {
    console.error("Error updating staff list:", error);
    setError("Failed to update staff list. Please try again later."); // Handle error
  } finally {
    setLoading(false); // End loading state
  }
};

// Function to fetch staff members for a specific workgroup
const fetchStaff = async (workgroupId) => {
  try {
    const token = await getToken();
    const response = await axios.get(`${backendURL}/workgroup-users`, {
      params: { workgroupId },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map((entry) => ({
      id: entry.id,
      ...entry,
    }));
  } catch (error) {
    console.error("Error fetching staff: ", error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Function to delete a staff member
export const deleteStaffMember = async (workgroupId, staffId) => {
  try {
    await serverDelete(`/workgroups/${workgroupId}/members/${staffId}`);
  } catch (error) {
    console.error("Error removing staff member: ", error);
    const apiError = error.response?.data?.error;
    throw new Error(apiError || error.message || "Failed to remove staff member");
  }
};

// Generic function to fetch any configuration
export const fetchConfig = async (workgroupId, configType) => {
  if (!workgroupId) {
    throw new Error(
      `Workgroup ID is required to fetch ${configType} configuration.`
    );
  }

  try {
    const response = await serverGet(
      `/workgroups/${workgroupId}/config/${configType}`
    );
    const config = response.data?.config;

    if (!config) {
      return {};
    }

    if (Array.isArray(config)) {
      console.warn(
        `fetchConfig: received schema array instead of saved config for "${configType}". Returning empty config object.`
      );
      return {};
    }

    if (typeof config !== "object") {
      throw new Error(
        `Configuration for ${configType} is missing or malformed.`
      );
    }

    return config;
  } catch (error) {
    const apiError = error.response?.data?.error;
    throw new Error(
      apiError || `Failed to fetch ${configType} configuration.`
    );
  }
};

// Generic function to sanitize any configuration value
const sanitizeConfigValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  // Convert to string and trim
  let sanitized = String(value).trim();
  
  // For AWS secret keys, remove all whitespace
  if (typeof value === 'string' && value.includes('aws_secret_access_key')) {
    sanitized = sanitized.replace(/\s+/g, '');
  }
  
  return sanitized;
};

// Generic function to save any configuration
export const saveConfig = async (workgroupId, configType, config) => {
  try {
    if (!workgroupId) {
      console.error(`No workgroup ID provided for saving ${configType} config`);
      throw new Error("Workgroup ID is required");
    }
    
    // Separate individual_settings from connection values
    const { individual_settings, ...connectionConfig } = config;

    // Sanitize connection values only (individual_settings is saved as-is)
    const sanitizedConfig = Object.entries(connectionConfig).reduce((acc, [key, value]) => {
      acc[key] = sanitizeConfigValue(value);
      return acc;
    }, {});

    // Special case for Salesforce token field name
    if (configType === 'salesforce' && sanitizedConfig.token) {
      sanitizedConfig.security_token = sanitizedConfig.token;
      delete sanitizedConfig.token;
    }

    // Re-attach individual_settings as a proper dict for saving
    const configToSave = { ...sanitizedConfig };
    if (individual_settings && typeof individual_settings === 'object') {
      configToSave.individual_settings = individual_settings;
    }

    // Save config to Firestore first
    await serverPut(`/workgroups/${workgroupId}/config/${configType}`, {
      config: configToSave,
    });

    try{
      const testConnectionResponse = await axios.post(`${llmURL}/v1/testConnectionDetails`, {
        workgroupId: workgroupId,
        config: sanitizedConfig,
        token: await getToken(),
        connectionType: configType
      });

      console.log(`testConnectionResponse: ${testConnectionResponse}`)
      console.log(`testConnectionResponse.status: ${testConnectionResponse.status}`)
      if (testConnectionResponse.status === 200) {
        console.log("Connection test successful");
      } else {
      console.error("Connection test failed");
      alert("Connection test failed, please check your connection details");
      return false;
    }
  }catch (error) {
    console.error("Error testing connection:", error);
    alert("Connection test failed, please check your connection details");
    return false;
  }

    console.log(`${configType} configuration saved successfully`);
    return true;
  } catch (error) {
    console.error(`Error saving ${configType} configuration:`, error);
    const apiError = error.response?.data?.error;
    if (apiError) {
      throw new Error(apiError);
    }
    throw error;
  }
};

// Function to fetch workgroups the user is part of, creating the document if none exists
export const fetchUserWorkgroups = async (userId) => {
  if (!userId) {
    return [];
  }

  try {
    const response = await serverGet(`/users/${userId}/workgroups`);
    const workgroups = response.data?.workgroups;
    if (Array.isArray(workgroups)) {
      return workgroups;
    }
    return [];
  } catch (error) {
    console.error("Error fetching user workgroups:", error);
    return [];
  }
};

export const fetchWorkgroupIdsAndNames = async(userId) => {
  const details = await fetchWorkgroupDetails(userId);
  return details.map(({ id, name }) => ({
    id,
    name: name || id,
  }));
}

export const fetchWorkgroupDetails = async (userId) => {
  if (!userId) {
    return [];
  }

  try {
    const response = await serverGet(`/users/${userId}/workgroups/details`);
    const workgroups = response.data?.workgroups;
    if (!Array.isArray(workgroups)) {
      return [];
    }
    return workgroups.map((group) => ({
      id: group.id,
      name: group.name || group.id,
      description: group.description || "",
      workgroup_types: group.workgroup_types,
    }));
  } catch (error) {
    console.error("Error fetching workgroup details:", error);
    return [];
  }
};

// Function to update the role of a user (staff or admin)
export const updateStaffRole = async (workgroupId, userId, role) => {
  try {
    await serverPatch(`/workgroups/${workgroupId}/members/${userId}`, {
      role,
    });
  } catch (error) {
    const apiError = error.response?.data?.error;
    throw new Error(apiError || "Failed to update role.");
  }
};

// Create a new workgroup and assign the current user as admin
export const createWorkgroup = async (workgroupName) => {
  console.log("Creating")
  const adminUid = await getUserId();
  try {
    const response = await axios.post(`${backendURL}/create-workgroup`, {
      workgroupName: workgroupName,
      adminUid: adminUid,
      token: await getToken()
    });

    if (response.status === 200) {
      console.log("Workgroup created and admin added successfully:", response.data.message);
    } else {
      console.error("Failed to create workgroup:", response.data.message);
      throw Error(response.data.message);
    }
  } catch (error) {
    console.error("Error creating workgroup and adding admin:", error);
    throw error;
  }
};

// Initialize the auth state listener and handle first sign-ups
export const initializeAuthListener = () => {
  onAuthStateChanged(async (user) => {
    if (!user) {
      return;
    }

    try {
      await serverPost(`/users/${user.uid}/first-login`, {
        email: user.email || null,
      });
    } catch (error) {
      console.error('Error processing first login:', error);
    }
  });
};

export const updateUserPreferences = async (userId, theme, sendKey, modelSelection = 0, ipProtection = false, queryMode = 'standard', ragMode = 'standard', ragK = 5, directRagMinWords = 3) => {
  try {
    const response = await serverPut(`/users/${userId}/preferences`, {
      theme,
      sendKey,
      modelSelection,
      ipProtection,
      queryMode,
      ragMode,
      ragK,
      directRagMinWords,
    });
    console.log("User preferences updated successfully");
    return response.data?.preferences ?? null;
  } catch (error) {
    console.error("Error updating user preferences:", error);
    throw error;
  }
}

export const getUserPreferences = async (userId) => {
  if (!userId) {
    console.error("User ID is required");
    return null;
  }

  try {
    const response = await serverGet(`/users/${userId}/preferences`);
    return response.data?.preferences ?? null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.warn("User preferences not found");
      return null;
    }
    console.error("Error retrieving user preferences:", error);
    return null;
  }
};

export const getUserRole = async (workgroupId, userId) => {
  if (!workgroupId || !userId) {
    return null;
  }

  try {
    const response = await serverGet(
      `/workgroups/${workgroupId}/members/${userId}`
    );
    return response.data?.role ?? null;
  } catch (error) {
    console.error("Error fetching user role:", error);
    return null;
  }
};

// Determine whether the current user has access to integration experiences
export const canAccessIntegrations = async (userId) => {
  if (!userId) {
    console.warn('canAccessIntegrations called without a user id');
    return false;
  }

  try {
    const response = await axios.post(`${backendURL}/canAccessIntegrations`, { 
      userId: userId,
      token: await getToken() 
    });
    if (response.status === 200) {
      console.log('User able to access integrations');
      return true
    }
    else {
      console.log(`User not able to access integrations: ${response.data}`);
    }  
  } catch (error) {
    console.error('Error checking integration access:', error);
  }

  return false;
};

export const canAccessDevs = async (userId) => {
  if (!userId) {
    console.warn('canAccessDevs called without a user id');
    return false;
  }

  try {
    const response = await axios.post(`${backendURL}/canAccessDevs`, {
      userId,
      token: await getToken()
    });

    if (response.status === 200 && response.data?.canAccess === true) {
      console.log('User able to access dev routes');
      return true;
    }

    console.log(`User not able to access dev routes: ${response.status}`, response.data);
  } catch (error) {
    console.error('Error checking dev access:', error);
  }

  return false;
};

export const canAccessCddi = async (userId) => {
  if (!userId) {
    console.warn('canAccessCddi called without a user id');
    return false;
  }

  try {
    const response = await axios.post(`${backendURL}/canAccessCddi`, {
      userId,
      token: await getToken()
    });

    if (response.status === 200 && response.data?.canAccess === true) {
      console.log('User able to access CDDI routes');
      return true;
    }

    console.log(`User not able to access CDDI routes: ${response.status}`, response.data);
  } catch (error) {
    console.error('Error checking CDDI access:', error);
  }

  return false;
};

export const getWorkgroupTypes = async (workgroupId) => {
  if (!workgroupId) {
    return ["sf"];
  }

  try {
    const response = await serverGet(`/workgroups/${workgroupId}/types`);
    const types = response.data?.workgroupTypes;
    if (Array.isArray(types) && types.length) {
      return types;
    }
    return ["sf"];
  } catch (error) {
    console.error("Error fetching workgroup types:", error);
    return ["sf"];
  }
};

export const saveWorkgroupTypes = async (workgroupId, workgroupTypes) => {
  try {
    if (!workgroupId) {
      console.error("No workgroup ID provided for saving workgroup types");
      throw new Error("Workgroup ID is required");
    }

    await serverPut(`/workgroups/${workgroupId}/types`, {
      workgroupTypes,
    });
    
    console.log(`Workgroup types saved successfully: ${workgroupTypes.join(', ')}`);
    return true;
  } catch (error) {
    console.error("Error saving workgroup types:", error);
    throw error;
  }
};

// Custom Prompt Cards Functions

const normalizeDate = (value, fallback = null) => {
  if (!value && value !== 0) {
    return fallback;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const normalizePromptCard = (card) => {
  if (!card || typeof card !== 'object') {
    return null;
  }
  return {
    ...card,
    createdAt: normalizeDate(card.createdAt, new Date()),
    updatedAt: normalizeDate(card.updatedAt, null),
  };
};

const normalizePromptGroup = (group) => {
  if (!group || typeof group !== 'object') {
    return null;
  }
  return {
    ...group,
    cards: Array.isArray(group.cards) ? group.cards : [],
    createdAt: normalizeDate(group.createdAt, new Date()),
    updatedAt: normalizeDate(group.updatedAt, null),
  };
};

// Create a new prompt card
export const createPromptCard = async (cardData, workgroupId = null) => {
  try {
    const response = await serverPost('/prompt-cards', {
      workgroupId: workgroupId || null,
      card: cardData,
    });
    const card = normalizePromptCard(response.data?.card);
    if (!card) {
      throw new Error('Prompt card response was malformed');
    }
    return card;
  } catch (error) {
    console.error("Error creating prompt card:", error);
    throw error;
  }
};

// Update an existing prompt card
export const updatePromptCard = async (cardId, cardData, workgroupId = null) => {
  try {
    const response = await serverPatch(`/prompt-cards/${cardId}`, {
      workgroupId: workgroupId || null,
      updates: cardData,
    });
    const card = normalizePromptCard(response.data?.card);
    if (!card) {
      throw new Error('Prompt card response was malformed');
    }
    return card;
  } catch (error) {
    console.error("Error updating prompt card:", error);
    throw error;
  }
};

// Delete a prompt card
export const deletePromptCard = async (cardId, workgroupId = null) => {
  try {
    const query = workgroupId ? `?workgroupId=${encodeURIComponent(workgroupId)}` : '';
    await serverDelete(`/prompt-cards/${cardId}${query}`);
    return true;
  } catch (error) {
    console.error("Error deleting prompt card:", error);
    throw error;
  }
};

// Fetch prompt cards (either personal or workgroup)
export const fetchPromptCards = async (workgroupId = null) => {
  try {
    const response = await serverGet('/prompt-cards', workgroupId ? { workgroupId } : undefined);
    const cards = Array.isArray(response.data?.cards) ? response.data.cards : [];
    return cards
      .map(normalizePromptCard)
      .filter(Boolean);
  } catch (error) {
    if (error.response?.status === 401) {
      console.warn("User must be authenticated to fetch prompt cards.");
      return [];
    }
    console.error("Error fetching prompt cards:", error);
    return [];
  }
};

// Create a new prompt group
export const createPromptGroup = async (groupData, workgroupId = null) => {
  try {
    const response = await serverPost('/prompt-groups', {
      workgroupId: workgroupId || null,
      group: groupData,
    });
    const group = normalizePromptGroup(response.data?.group);
    if (!group) {
      throw new Error('Prompt group response was malformed');
    }
    return group;
  } catch (error) {
    console.error("Error creating prompt group:", error);
    throw error;
  }
};

// Update a prompt group
export const updatePromptGroup = async (groupId, groupData, workgroupId = null) => {
  try {
    const response = await serverPatch(`/prompt-groups/${groupId}`, {
      workgroupId: workgroupId || null,
      updates: groupData,
    });
    const group = normalizePromptGroup(response.data?.group);
    if (!group) {
      throw new Error('Prompt group response was malformed');
    }
    return group;
  } catch (error) {
    console.error("Error updating prompt group:", error);
    throw error;
  }
};

// Delete a prompt group
export const deletePromptGroup = async (groupId, workgroupId = null) => {
  try {
    const query = workgroupId ? `?workgroupId=${encodeURIComponent(workgroupId)}` : '';
    await serverDelete(`/prompt-groups/${groupId}${query}`);
    return true;
  } catch (error) {
    console.error("Error deleting prompt group:", error);
    throw error;
  }
};

// Fetch prompt groups (either personal or workgroup)
export const fetchPromptGroups = async (workgroupId = null) => {
  try {
    const response = await serverGet('/prompt-groups', workgroupId ? { workgroupId } : undefined);
    const groups = Array.isArray(response.data?.groups) ? response.data.groups : [];
    return groups
      .map(normalizePromptGroup)
      .filter(Boolean);
  } catch (error) {
    if (error.response?.status === 401) {
      console.warn("User must be authenticated to fetch prompt groups.");
      return [];
    }
    console.error("Error fetching prompt groups:", error);
    return [];
  }
};

// Add a card to a group
export const addCardToGroup = async (groupId, cardId, workgroupId = null) => {
  try {
    const response = await serverPost(`/prompt-groups/${groupId}/cards`, {
      workgroupId: workgroupId || null,
      cardId,
    });
    const group = normalizePromptGroup(response.data?.group);
    if (!group) {
      throw new Error('Prompt group response was malformed');
    }
    return group;
  } catch (error) {
    console.error("Error adding card to group:", error);
    throw error;
  }
};

// Initialize default prompt group if none exists
export const initializeDefaultPromptGroups = async (workgroupId = null) => {
  try {
    const response = await serverPost('/prompt-groups/initialize', {
      workgroupId: workgroupId || null,
    });
    return Boolean(response.data?.created);
  } catch (error) {
    console.error("Error initializing default prompt groups:", error);
    throw error; // Re-throw to better track errors
  }
};

// Check if user has exceeded their workgroup limit based on their license
const checkWorkgroupLimit = async (userId) => {
  try {
    // Call server endpoint to check license and workgroup count
    const response = await axios.post(`${backendURL}/check-workgroup-limit`, {
      userId: userId,
      token: await getToken()
    });

    if (response.status !== 200) {
      throw new Error("Failed to check workgroup limit");
    }

    return response.data;

  } catch (error) {
    console.error("Error checking workgroup limit:", error);
    throw error;
  }
};

export const checkLicenseAllowsWorkgroups = async (userId) => {
  const workgroup_stats = await checkWorkgroupLimit(userId);
  console.log("workgroup_stats: " + workgroup_stats.isUnderLimit);
  return workgroup_stats.isUnderLimit;
}

// Save onboarding state for a user
export const saveOnboardingState = async (userId, onboardingState) => {
  try {
    await serverPut(`/users/${userId}/onboarding-state`, {
      onboardingState,
    });
    return true;
  } catch (error) {
    console.error("Error saving onboarding state:", error);
    throw error;
  }
};

// Load onboarding state for a user
export const loadOnboardingState = async (userId) => {
  try {
    const response = await serverGet(`/users/${userId}/onboarding-state`);
    return response.data?.onboardingState ?? null;
  } catch (error) {
    console.error("Error loading onboarding state:", error);
    throw error;
  }
};

export const clearOnboardingState = async (userId) => {
  try {
    await serverDelete(`/users/${userId}/onboarding-state`);
    return true;
  } catch (error) {
    console.error("Error clearing onboarding state:", error);
    throw error;
  }
};

// Check if user has completed onboarding
export const isOnboardingComplete = async (userId) => {
  try {
    const response = await serverGet(`/users/${userId}/onboarding-status`);
    const data = response.data || {};
    console.log('isOnboardingComplete: Status response:', data);
    return Boolean(data.complete);
  } catch (error) {
    console.error("isOnboardingComplete: Error checking onboarding status:", error);
    return false; // On error, assume needs onboarding
  }
};

// Create example prompt group and cards for onboarding completion
export const createExamplePrompts = async (workgroupId, selectedTypes) => {
  try {
    console.log('Creating example prompts for workgroup:', workgroupId, 'with types:', selectedTypes);
    
    // Import the example prompts configuration
    const { getExamplesForTypes } = await import('../config/examplePrompts.js');
    const examples = getExamplesForTypes(selectedTypes);
    
    if (examples.length === 0) {
      console.log('No examples found for selected types:', selectedTypes);
      return;
    }
    
    // Create the "Examples" prompt group
    const exampleGroup = await createPromptGroup({
      name: "Examples",
      cards: [],
      isDefault: false
    }, workgroupId);
    
    console.log('Created example group:', exampleGroup.id);
    
    // Create example prompt cards and add them to the group
    const cardIds = [];
    for (const example of examples) {
      try {
        const card = await createPromptCard({
          title: example.title,
          description: example.description,
          query: example.query
        }, workgroupId);
        
        cardIds.push(card.id);
        console.log('Created example card:', card.id, example.title);
      } catch (cardError) {
        console.error('Error creating example card:', cardError);
      }
    }
    
    // Add all cards to the example group
    if (cardIds.length > 0) {
      await updatePromptGroup(exampleGroup.id, {
        ...exampleGroup,
        cards: cardIds
      }, workgroupId);
      console.log('Added', cardIds.length, 'example cards to group');
    }
    
    console.log('Successfully created example prompts for workgroup:', workgroupId);
  } catch (error) {
    console.error('Error creating example prompts:', error);
    // Don't throw error to avoid breaking onboarding flow
  }
};

const normalizeTestId = (testId) => String(testId).replace('/', '_');

export const fetchIntegrationDocument = async (integrationId, workgroupId) => {
  try {
    if (!workgroupId) {
      console.warn('fetchIntegrationDocument called without workgroupId; skipping request.');
      return null;
    }
    const params = { workgroupId };
    const response = await serverGet(`/v1/integrations/${integrationId}/config`, params);
    const data = response.data;
    if (!data || typeof data !== 'object') {
      return null;
    }
    return {
      id: typeof data.id === 'string' ? data.id : null,
      name:
        typeof data.name === 'string' && data.name.trim() !== '' ? data.name : null,
      config_schema: data.config_schema,
    };
  } catch (error) {
    console.error(`Error fetching integration ${integrationId}:`, error);
    throw error;
  }
};

export const fetchIntegrationsCollection = async () => {
  try {
    const response = await serverGet('/v1/integrations');
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map((item) => ({
      id: item.id,
      ...item,
    }));
  } catch (error) {
    console.error('Error fetching integrations collection:', error);
    throw error;
  }
};

export const fetchIntegrationSuggestedPrompts = async (integrationId, workgroupId) => {
  try {
    if (!integrationId) {
      throw new Error('Integration ID is required');
    }
    if (!workgroupId) {
      throw new Error('Workgroup ID is required');
    }

    const params = { workgroup: workgroupId };
    const response = await serverGet(`/integrations/${integrationId}/suggested-prompts`, params);
    const data = response?.data || {};
    const suggestedPrompts = Array.isArray(data.suggested_prompts) ? data.suggested_prompts : [];
    const name =
      typeof data.name === 'string' && data.name.trim() !== '' ? data.name.trim() : undefined;
    return { suggested_prompts: suggestedPrompts, name };
  } catch (error) {
    console.error(`Error fetching suggested prompts for integration ${integrationId}:`, error);
    throw error;
  }
};

export const fetchIntegrationTests = async (integrationId) => {
  try {
    const response = await serverGet(`/integrations/${integrationId}/tests`);
    const tests = Array.isArray(response.data?.tests) ? response.data.tests : [];
    return tests;
  } catch (error) {
    console.error(`Error fetching tests for integration ${integrationId}:`, error);
    throw error;
  }
};

export const fetchIntegrationCheckpoints = async (integrationId, testId) => {
  try {
    const response = await serverGet(
      `/integrations/${integrationId}/tests/${normalizeTestId(testId)}/checkpoints`
    );
    const checkpoints = Array.isArray(response.data?.checkpoints)
      ? response.data.checkpoints
      : [];
    return checkpoints
      .map((item) => ({
        ...item,
        created_at: resolveTimestamp(item.created_at, new Date()),
        updated_at: resolveTimestamp(item.updated_at, null),
      }));
  } catch (error) {
    console.error(
      `Error fetching checkpoints for integration ${integrationId}, test ${testId}:`,
      error
    );
    throw error;
  }
};

export const fetchIntegrationImprovements = async (integrationId, testId) => {
  try {
    const response = await serverGet(
      `/integrations/${integrationId}/tests/${normalizeTestId(testId)}/improvements`
    );
    const improvements = Array.isArray(response.data?.improvements)
      ? response.data.improvements
      : [];
    const normalized = improvements.map((item) => {
      const created = resolveTimestamp(item.created_at, null);
      const updated = resolveTimestamp(item.updated_at, null);
      return {
        ...item,
        created_at: created,
        updated_at: updated,
      };
    });
    return normalized.sort((a, b) => {
      const ta = a.created_at instanceof Date ? a.created_at.getTime() : 0;
      const tb = b.created_at instanceof Date ? b.created_at.getTime() : 0;
      return tb - ta;
    });
  } catch (error) {
    console.error(
      `Error fetching improvements for integration ${integrationId}, test ${testId}:`,
      error
    );
    throw error;
  }
};

export const fetchIntegrationBatchDetails = async (integrationId, testId, batchId) => {
  try {
    const response = await serverGet(
      `/integrations/${integrationId}/tests/${normalizeTestId(testId)}/batches/${String(batchId)}`
    );
    return response.data?.batch || {};
  } catch (error) {
    console.error(
      `Error fetching batch ${batchId} for integration ${integrationId}, test ${testId}:`,
      error
    );
    throw error;
  }
};

const resolveTimestamp = (value, fallback) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value.toDate === 'function') {
    try {
      const asDate = value.toDate();
      if (asDate instanceof Date && !Number.isNaN(asDate.getTime())) {
        return asDate;
      }
    } catch (_) {
      // Ignore conversion failure and continue with other strategies
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  if (
    value &&
    typeof value.seconds === 'number' &&
    typeof value.nanoseconds === 'number'
  ) {
    try {
      return new Date(
        value.seconds * 1000 + Math.floor(value.nanoseconds / 1e6)
      );
    } catch (_) {
      // Ignore conversion failure
    }
  }

  return fallback;
};

const normalizeDisplayChallengeDoc = (id, data = {}) => {
  const hasCreatedAt = Object.prototype.hasOwnProperty.call(data, 'created_at');
  const hasUpdatedAt = Object.prototype.hasOwnProperty.call(data, 'updated_at');

  return {
    id,
    ...data,
    created_at: resolveTimestamp(hasCreatedAt ? data.created_at : null, null),
    updated_at: resolveTimestamp(hasUpdatedAt ? data.updated_at : null, null),
  };
};

const loadDisplayChallenge = async (integrationId, challengeId) => {
  if (!integrationId || !challengeId) {
    throw new Error('Integration ID and challenge ID are required.');
  }

  try {
    const response = await serverGet(
      `/integrations/${integrationId}/display-challenges/${challengeId}`
    );
    const challenge = response.data?.challenge;
    if (!challenge || typeof challenge !== 'object') {
      return null;
    }
    return normalizeDisplayChallengeDoc(challenge.id || challengeId, challenge);
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error(
      `Error fetching display challenge ${challengeId} for integration ${integrationId}:`,
      error
    );
    throw error;
  }
};

export const fetchIntegrationDisplayChallenge = loadDisplayChallenge;
export const fetchDisplayChallengeById = loadDisplayChallenge;

export const fetchReducedDisplayChallengeById = async (integrationId, challengeId) => {
  if (!integrationId || !challengeId) {
    throw new Error('Integration ID and challenge ID are required.');
  }

  try {
    const response = await serverGet(
      `/integrations/${integrationId}/display-challenges/${challengeId}/reduced`
    );
    const challenge = response.data?.challenge;
    if (!challenge || typeof challenge !== 'object') {
      return null;
    }
    return normalizeDisplayChallengeDoc(challenge.id || challengeId, challenge);
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error(
      `Error fetching reduced display challenge ${challengeId} for integration ${integrationId}:`,
      error
    );
    throw error;
  }
};

export const fetchBatchAnswers = async (
  integrationId,
  testId,
  batchId,
  checkpointId = null
) => {
  try {
    const params = checkpointId ? { checkpointId } : undefined;
    const response = await serverGet(
      `/integrations/${integrationId}/tests/${normalizeTestId(testId)}/batches/${String(batchId)}/answers`,
      params
    );
    const answers = Array.isArray(response.data?.answers) ? response.data.answers : [];
    return answers;
  } catch (error) {
    console.error(
      `Error fetching legacy batch results for integration ${integrationId}, test ${testId}, batch ${batchId}:`,
      error
    );
    throw error;
  }
};

// Paged fetch for display challenges (newest first)
export const fetchDisplayChallengesPage = async (integrationId, pageSize = 5, afterDoc = null) => {
  try {
    const safePageSize = Math.max(1, parseInt(pageSize, 10) || 5);
    const params = { pageSize: safePageSize };
    if (afterDoc) {
      params.cursor = afterDoc;
    }

    const response = await serverGet(
      `/integrations/${integrationId}/display-challenges`,
      params
    );
    const data = response.data || {};

    const items = Array.isArray(data.items)
      ? data.items.map((item = {}) =>
          normalizeDisplayChallengeDoc(item.id || '', item)
        )
      : [];

    const lastDoc = data.nextCursor || null;
    const hasMore = Boolean(data.hasMore);

    console.log(
      '[Paged] Loaded',
      items.length,
      'items. hasMore:',
      hasMore,
      'hasCursor:',
      Boolean(lastDoc)
    );

    return { items, lastDoc, hasMore };
  } catch (error) {
    console.error('[Paged] Error fetching display challenges:', error);
    throw error;
  }
};

// Fetch the live challenge document by id (real-time monitoring)
export const fetchChallengeById = async (challengeId) => {
  if (!challengeId) {
    return null;
  }

  try {
    const response = await serverGet(`/challenges/${challengeId}`);
    const challenge = response.data?.challenge;
    if (!challenge || typeof challenge !== 'object') {
      return null;
    }

    const normalized = {
      id: challenge.id || challengeId,
      ...challenge,
    };

    if ('created_at' in normalized) {
      normalized.created_at = resolveTimestamp(normalized.created_at, null);
    }
    if ('updated_at' in normalized) {
      normalized.updated_at = resolveTimestamp(normalized.updated_at, null);
    }

    return normalized;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Error fetching challenge by id:', error);
    throw error;
  }
};
    
